import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  ButtonBase,
  Divider,
} from '@mui/material';
import {
  ClockIcons,
  ListCheckIcons,
  SettingsIcons,
} from '../../../../icons/icons';
import { Button, MainLayout } from '../../../../ui';
import { NoDataState } from '../../../../ui/components/Common/NoData/NoDataState';
import { DatePicker } from '../../../../ui/input/date_picker/date_picker';
import { CandidateStatusEnum } from '../../../../utils/helpers/candidateStatus';
import { Tabs } from '../../../assessment/view/tabs/tabs';
import { AssessmentResults } from '../../../assessment/view/view_assessment/assessment_result_list/assessment_result_list';
import { Settings } from '../../../assessment/view/view_assessment/settings/settings';
import { CandidateInvites } from '../JobOpeningDetails/AutoAssessmentTab/TabContent/CandidateInvites';
import { InvitationTemplate } from '../JobOpeningDetails/AutoAssessmentTab/TabContent/InvitationTemplate';
import { AutoAssessmentResultsDetails } from './Containers/AssesmentResultsDetails';
import { useAssessmentQuestions } from './Hooks';

export const AssessmentQuestions: React.FC = () => {
  const location = useLocation();
  const getAssessmentTabFromUrl = (): number => {
    const searchParams = new URLSearchParams(location.search);
    const tabValue = searchParams.get('assessmentTab');
    const tabIndex = parseInt(tabValue || '0');
    return isNaN(tabIndex) ? 0 : tabIndex;
  };

  const getSidebarTabFromUrl = (): 'Config' | 'Results' => {
    const searchParams = new URLSearchParams(location.search);
    return (searchParams.get('sidebarTab') as 'Config' | 'Results') || 'Config';
  };

  const {
    activeTab,
    setActiveTab: hookSetActiveTab,
    sidebarTab,
    setSidebarTab,
    tabTitles,
    autoAssessment,
    assessmentUserListResponse,
    overviewResultResponse,
    highestResultScore,
    updateAssessmentUserList,
    assessmentState,
    assessmentFormInfo,
    changeDate,
    handleDataChange,
    // Invite Candidates
    candidatesList,
    selectedCandidates,
    selectedCandidateIds,
    limit,
    loading,
    sort,
    sendInvites,
    handleRemoveCandidate,
    handleSortChange,
    isRowSelected,
    handleSelectRow,
    handleSelectAll,
    handleStatusListChange,
    onPageChange,
    isAllSelected,
    isIndeterminate,
    pageNumber,
    totalPages,
    totalRecords,
    candidateInviteObject,
    setLocalName,
    setLocalEmail,
    localName,
    localEmail,
  } = useAssessmentQuestions();

  // URL-sync wrapper for setActiveTab
  const setActiveTab = (tabIndex: number | string) => {
    const index =
      typeof tabIndex === 'string' ? tabTitles.indexOf(tabIndex) : tabIndex;
    if (index === -1) return;

    hookSetActiveTab(index);
    const searchParams = new URLSearchParams(location.search);

    // Always ensure main tab is set correctly for Auto-Assessments
    searchParams.set('tab', 'Auto-Assessments');
    searchParams.set('assessmentTab', index.toString());
    searchParams.set('sidebarTab', sidebarTab);

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const handleSidebarChange = (tab: 'Config' | 'Results') => {
    setSidebarTab(tab);
    hookSetActiveTab(0); // Reset sub-tab when switching sidebar

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', 'Auto-Assessments');
    searchParams.set('sidebarTab', tab);
    searchParams.set('assessmentTab', '0');

    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  const updateEmailTemplateState = (_updatedJsonTemplate: string) => {
    // This could update local state if needed, but the hook handles fetch
  };

  // Sync with URL on location change
  React.useEffect(() => {
    const tabFromUrl = getAssessmentTabFromUrl();
    const sidebarTabFromUrl = getSidebarTabFromUrl();

    if (tabFromUrl !== activeTab) {
      hookSetActiveTab(tabFromUrl);
    }

    if (sidebarTabFromUrl !== sidebarTab) {
      setSidebarTab(sidebarTabFromUrl);
    }
  }, [location.search]);

  const [selectedResult, setSelectedResult] =
    useState<{
      assessmentId: string;
      userId: string;
      id: string;
    } | null>(null);

  const sidebarButtons = useMemo(
    () => [
      {
        label: 'Config',
        disabled: false,
        Icon: SettingsIcons,
      },
      {
        label: 'Results',
        disabled: false,
        Icon: ListCheckIcons,
      },
    ],
    [],
  );

  const renderTabContent = () => {
    const currentTabTitle = tabTitles[activeTab];
    if (
      !autoAssessment?.assessment?.assessmentType ||
      autoAssessment?.assessment?.assessmentType !== 'candidate' ||
      !autoAssessment?.assessment?.id
    ) {
      return null;
    }

    const baseProps = {
      preScreenQuestions: autoAssessment?.assessment?.preScreen
        ? autoAssessment?.assessment.preScreen
        : [],
      assessmentId: autoAssessment.assessment.id,
      userResults: assessmentUserListResponse?.assessmentUserList || [],
      completionRatio: assessmentUserListResponse?.completionRatio || '',
      highestScore: highestResultScore || 0,
      updateAssessmentUserList,
      behaviorSkills: overviewResultResponse?.behaviorSkills || [],
      skills: overviewResultResponse?.skills || [],
      countCompleted: assessmentUserListResponse?.countCompleted || 0,
      countTotal: assessmentUserListResponse?.countTotal || 0,
      isAutoAssessment: true,
      onResultClick: (assessmentResult: any) => {
        setSelectedResult({
          assessmentId: assessmentResult.assessmentId,
          userId: assessmentResult.userId,
          id: assessmentResult.id,
        });
      },
    };

    switch (currentTabTitle) {
      case 'Settings':
        return (
          <>
            {assessmentFormInfo && (
              <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', lg: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', lg: 'center' },
                    gap: 2,
                    px: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: 'action.hover',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <ClockIcons size={16} color="#475569" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Assessment Availability
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Specify the dates when this assessment will be accessible.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: { xs: '100%', sm: 250 } }}>
                      <DatePicker
                        startDate={assessmentFormInfo.startDate}
                        endDate={assessmentFormInfo.endDate}
                        onChange={changeDate}
                      />
                    </Box>
                    <Button
                      variant="primary"
                      label="Update"
                      onClick={handleDataChange}
                      sx={{ height: 32, px: 2, borderRadius: 2, fontSize: "0.75rem", fontWeight: 700 }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Settings
              assessmentType={'candidate'}
              assessmentId={autoAssessment.assessment.id}
              skipSpecificSettings={true}
              setActiveTab={setActiveTab as any}
            />
          </>
        );
      case 'Invitation Template':
        return (
          <InvitationTemplate
            setActiveTab={setActiveTab as any}
            emailTemplate={autoAssessment.emailJsonTemplate}
            updateEmailTemplateState={updateEmailTemplateState}
          />
        );
      case 'Candidates':
        return (
          <CandidateInvites
            handleSendInvites={sendInvites}
            handleRemoveCandidate={handleRemoveCandidate}
            candidatesList={candidatesList}
            loading={loading}
            pageNumber={pageNumber}
            totalPages={totalPages}
            totalRecords={totalRecords}
            selectedCandidates={selectedCandidates}
            selectedCandidateIds={selectedCandidateIds}
            limit={limit}
            handleSortChange={handleSortChange}
            isRowSelected={isRowSelected}
            candidateInviteObject={candidateInviteObject}
            handleSelectRow={handleSelectRow}
            sort={sort}
            handleSelectAll={handleSelectAll}
            handleStatusListChange={handleStatusListChange}
            onPageChange={onPageChange}
            setLocalName={setLocalName}
            setLocalEmail={setLocalEmail}
            localName={localName}
            localEmail={localEmail}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
          />
        );
      case 'Results':
        if (!assessmentState.hasData) {
          return (
            <NoDataState
              title="No Assessment Results Yet"
              message="Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments."
              illustration="assessment"
            />
          );
        }
        return <AssessmentResults {...baseProps} />;

      case CandidateStatusEnum.WAITLISTED:
        if (!assessmentState.hasData) {
          return (
            <NoDataState
              title="No Assessment Results Yet"
              message="Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments."
              illustration="assessment"
            />
          );
        }
        return (
          <AssessmentResults
            {...baseProps}
            candidateStatus={CandidateStatusEnum.WAITLISTED}
          />
        );

      case CandidateStatusEnum.SHORTLISTED:
        if (!assessmentState.hasData) {
          return (
            <NoDataState
              title="No Assessment Results Yet"
              message="Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments."
              illustration="assessment"
            />
          );
        }
        return (
          <AssessmentResults
            {...baseProps}
            candidateStatus={CandidateStatusEnum.SHORTLISTED}
          />
        );

      case CandidateStatusEnum.UNSUCCESSFUL:
        if (!assessmentState.hasData) {
          return (
            <NoDataState
              title="No Assessment Results Yet"
              message="Start by inviting candidates to take the assessment. Results will appear here once candidates begin completing their assessments."
              illustration="assessment"
            />
          );
        }
        return (
          <AssessmentResults
            {...baseProps}
            candidateStatus={CandidateStatusEnum.UNSUCCESSFUL}
          />
        );

      default:
        return null;
    }
  };

  if (assessmentState.isLoading) {
    return (
      <NoDataState
        title="Loading Assessment Results"
        message="Please wait while we fetch the assessment data..."
        illustration="loading"
      />
    );
  }

  if (assessmentState.error) {
    return (
      <NoDataState
        title="Unable to Load Assessment Data"
        message={assessmentState.error}
        actionLabel="Retry"
        onAction={() => window.location.reload()}
        illustration="error"
      />
    );
  }

  return (
    <Box
      sx={{
        mt: { xs: 1.5, sm: 2.5 },
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        gap: { xs: 2, lg: 3 },
      }}
    >
      {!selectedResult && (
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            width: { xs: '100%', lg: 64 },
            gap: { xs: 1, lg: 1.5 },
            overflowX: 'auto',
            borderRadius: { xs: 2, lg: 3 },
            border: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            p: 1,
            position: { lg: 'sticky' },
            top: { lg: 16 },
            height: { lg: 'fit-content' },
            flexShrink: 0,
            flexDirection: { xs: 'row', lg: 'column' },
            py: { lg: 2 },
          }}
        >
          {sidebarButtons.map((button, index) => {
            if (button.disabled) return null;
            const isActive = sidebarTab === button.label;
            const Icon = button.Icon;
            return (
              <ButtonBase
                key={index}
                onClick={() => handleSidebarChange(button.label as any)}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'row', lg: 'column' },
                  alignItems: 'center',
                  gap: { xs: 1, lg: 0.5 },
                  px: 0.5,
                  minWidth: { xs: 'max-content', lg: 'auto' },
                  borderRadius: 2,
                  transition: 'all 0.2s',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 32, lg: 40 },
                    height: { xs: 32, lg: 40 },
                    borderRadius: 2,
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    bgcolor: isActive ? 'primary.main' : 'background.paper',
                    color: isActive ? 'primary.contrastText' : 'text.secondary',
                    boxShadow: isActive ? 2 : 0,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: isActive ? 'primary.contrastText' : 'primary.main',
                    },
                  }}
                >
                  <Icon size={16} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: '9px', lg: '8px' },
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    textAlign: { xs: 'left', lg: 'center' },
                  }}
                >
                  {button.label}
                </Typography>
              </ButtonBase>
            );
          })}
        </Paper>
      )}
      <MainLayout>
        {selectedResult ? (
          <AutoAssessmentResultsDetails
            assessmentId={selectedResult.assessmentId}
            userId={selectedResult.userId}
            id={selectedResult.id}
            backToClick={() => setSelectedResult(null)}
          />
        ) : (
          <>
            <Tabs
              tabTitles={tabTitles}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
            {renderTabContent()}
          </>
        )}
      </MainLayout>
    </Box>
  );
};

export default AssessmentQuestions;
